<template>
  <div
    v-if="cart || quote"
    v-responsive-classes
    :class="{ 'no-header': !showHeader }"
    class="cart-wrapper relative flex-column width-expand overflow-hidden">
    <card
      v-if="showHeader"
      class="cart-header overflow-hidden flex-column padding-remove box-shadow-1 z-level-2 relative">
      <template #body>
        <div class="card__body padding-remove margin-remove">
          <div
            class="flex-row flex-between flex-gap-l flex-middle padding-m bg-inverse c-bg">
            <div class="flex-column width-expand">
              <div class="text-l flex-between flex-wrap width-expand">
                <span class="inline-block text-bold">
                  {{ translateUcFirst("ecommerce.cart.total") }}&nbsp; ({{
                    translateNumber(totalItemQuantity)
                  }}&nbsp;{{ translateUcFirst("ecommerce.cart.items") }}):
                  &nbsp;
                </span>
                <span class="inline-block text-bold">
                  {{
                    translateNumber(computedQuote?.grandTotal, "currency", {
                      currency: computedQuote?.currency,
                    })
                  }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="showHeaderFeatures"
            class="cart-header__features padding-m">
            <transition enter-active-class="animate__fadeIn animate--fast">
              <div v-show="messagesExpanded" class="">
                <slot name="header-features">
                  <div
                    v-for="(message, index) in finalMessages"
                    :key="index"
                    class="text-s flex-column gap-s">
                    <icon-text :icon="message.icon">{{
                      message.content
                    }}</icon-text>
                  </div>
                </slot>
              </div>
            </transition>
          </div>
        </div>
      </template>
    </card>

    <div
      class="items-container z-level-0 static overflow-y-auto"
      style="max-height: 50vh">
      <div
        v-for="(item, index) in computedQuote.items"
        v-if="!isEmpty"
        :key="index"
        class="cart-item padding-m-horizontal padding-m-vertical flex-column flex-gap-m">
        <div class="cart-item__desc-row">
          <div class="flex-row gap-m">
            <div style="max-height: 80px">
              <img
                class="cart-item__image max-size-parent border-thin"
                :src="item.cartItem.Product.introImage"
                :alt="
                  translate('core.genericAlt.imageOf', {
                    thing: item.cartItem.Product.name,
                  })
                "
                style="" />
            </div>
            <div class="flex-column width-expand">
              <div class="text-bold text-m">
                {{ item.cartItem.Product.name }}
              </div>
              <div class="flex-column text-s">
                <slot name="cart-item-types">
                  <component
                    :is="productTypeComponents[type.type]"
                    v-for="type in getProductTypes(item)"
                    :item="item"
                    :quote="computedQuote">
                  </component>
                </slot>
              </div>

              <slot
                name="cart-item-extra"
                v-bind="{ item, product: item.cartItem.Product }">
                <div class="text-s flex-row flex-gap-s"></div>
              </slot>
              <slot
                name="cart-item-controls"
                v-bind="{ item, product: item.cartItem.Product }">
                <div
                  class="cart-item__control-row flex-row flex-stretch flex-gap-m flex-between margin-m-top">
                  <div class="flex-column flex-between width-expand gap-s">
                    <slot
                      name="cart-item-quantity"
                      v-bind="{ item, product: item.cartItem.Product }">
                      <div v-if="!readOnly" class="flex-row flex-gap-m">
                        <form-button
                          size="s"
                          :disabled="true"
                          icon="pencil"
                          :show-slot="false"
                          :slim="true"></form-button>
                        <div class="block">
                          <form-input
                            v-model="item.quantity"
                            :disabled="readOnly"
                            size="s"
                            class="inline-block quantity-controls max-width-100"
                            controls-class="border-remove"
                            type="justoppUnits"
                            :step-size="1"
                            @update:model-value="updateCart(item)"></form-input>
                        </div>
                      </div>
                      <span
                        v-else
                        class="block margin-xs-top text-start text-s">
                        {{ translateUcFirst("ecommerce.cart.quantity") }}:
                        {{ translateNumber(item.quantity) }}
                      </span>
                    </slot>
                  </div>
                  <div class="flex-column text-inline-end">
                    <slot name="cart-item-price">
                      <span class="">{{
                        translateNumber(item.grandTotal, "currency", {
                          currency: item.cartItem.Product.currency,
                        })
                      }}</span>
                    </slot>

                    <slot
                      name="cart-item-unit-price"
                      v-bind="{ item, product: item.cartItem.Product }">
                      <span class="text-xs" style="white-space: nowrap;"
                        >{{
                          translateNumber(item.unitPrice, "currency", {
                            currency: item.cartItem.Product.currency,
                          })
                        }}
                        / {{ translate("ecommerce.cart.unit") }}
                      </span>
                    </slot>
                  </div>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isEmpty"
        class="no-items padding-max-vertical padding-m-horizontal text-center">
        <h3 class="block text-center margin-remove">
          {{ translateUcFirst("ecommerce.cart.cartEmptyExplain") }}
        </h3>
      </div>
    </div>

    <div
      class="extra-items-container padding-m-horizontal padding-l-vertical bg-gray-1 animate__fadeIn"
      v-if="
        computedQuote &&
        typeof computedQuote?.additionalItems === 'object' &&
        computedQuote?.additionalItems &&
        Object.values(computedQuote?.additionalItems).length > 0
      ">

      <div class="flex-column gap-xs">
        <div class="text-s gap-m flex-between flex-wrap additional-item additional-item--subtotal">
          <span class="label line-height-090">
            {{tu('ecommerce.cart.subTotal')}}
          </span>
          <span class="price block  max-width-100 min-width-100">{{ translateNumber(computedQuote.grandItemsTotal, "currency") }}</span>
        </div>
        <div class="text-s gap-m flex-between flex-wrap additional-item " v-for="item of computedQuote.additionalItems">
          <span class="label line-height-090">{{ t(item.label) }}</span>
          <span class="price block  max-width-100 min-width-100">{{ translateNumber(item.grandTotal, "currency") }}</span>
        </div>
        <div class="text-m  bold gap-m flex-between flex-wrap  additional-item additional-item--grand-total">
          <span class="label line-height-090"> {{tu('ecommerce.cart.finalGrandTotal')}}</span>
          <span class="price block  max-width-100 min-width-100">{{ translateNumber(computedQuote.grandTotal, "currency") }}</span>
        </div>
      </div>

    </div>
    <card
      v-if="showActionBar"
      class="flex-column cart-action-bar padding-remove box-shadow-2 bg-inverse c-lead z-level-2 relative">
      <template #body>
        <div class="card__body margin-remove flex-center flex-middle padding-m">
          <slot name="summary-action">
            <block class="flex gap-m flex-center width-expand flex-wrap">
              <form-button
                icon-end="chevron-inline-end"
                :disabled="isEmpty"
                text="ecommerce.cart.checkoutCta"
                theme="lead"
                @click="$router.push({ name: 'store-checkout' })"></form-button>
            </block>
          </slot>
        </div>
      </template>
    </card>
    <div v-else="" class="pseudo-action-bar" style=""></div>
  </div>
</template>

<script>
import asyncComponentFactory from "@/client/extensions/modules/asyncComponentFactory";

export default {
  props: {
    cart: {
      type: Object,
      default: () => ({}),
    },
    quote: {
      type: [Object, Boolean],
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showHeaderFeatures: {
      type: Boolean,
      default: true,
    },
    showActionBar: {
      type: Boolean,
      default: true,
    },
    messages: {
      type: [Array, Boolean],
      default: false,
    },
  },
  data: function () {
    let productTypeComponents = {};
    let rawTypes = config.ecommerce.productTypes.activeProductTypes.map(
      (item) => item.value
    );
    for (const type of rawTypes) {
      let componentTarget = utilities.ucFirst(type);
      productTypeComponents[type] = asyncComponentFactory(
        `components/ecommerce/cart/productTypeDisplay/${componentTarget}.vue`,
        true
      );
    }

    let defaultMessages = [
      {
        content: this.tu("ecommerce.cart.defaultFeature1"),
        icon: "user",
      },
      {
        content: this.tu("ecommerce.cart.defaultFeature2"),
        icon: "shield-check",
      },
    ];
    return {
      productTypeComponents,
      messagesExpanded: true,
      finalMessages: this.messages ? this.messages : defaultMessages,
    };
  },
  computed: {
    isEmpty() {
      return this.totalItemQuantity < 1;
    },
    totalItemQuantity() {
      if (this.computedQuote) {
        return this.computedQuote.items.reduce((total, item) => {
          return +total + item.quantity;
        }, 0);
      } else {
        return 0;
      }
    },
    computedQuote() {
      if (this?.quote) {
        return this.quote;
      }
      if ( this?.cart?.quote) {
        return this?.cart?.quote;
      }

      if ( this?.cart?.Quote) {
        return this?.cart?.Quote;
      }
      return this?.cart?.quote;
    },
  },
  methods: {
    getProductTypes(item) {
      let product = item.cartItem.Product;
      let rawTypes = config.ecommerce.productTypes.activeProductTypes.map(
        (item) => item.value
      );

      let result = [];
      rawTypes.forEach((type) => {
        let typeKey = `${utilities.ucFirst(type)}ProductType`;
        if (product[typeKey]) {
          result.push({
            type: type,
            typeInstance: product[typeKey],
          });
        }
      });
      return result;
    },
  },
};
</script>

<style lang="scss" scoped>
.cart-wrapper {
  .cart-header__features {
    border-inline-start: 1px solid var(--c-bg-4);
    border-inline-end: 1px solid var(--c-bg-4);
  }

  .items-container {
    border-inline-start: 1px solid var(--c-bg-4);
    border-inline-end: 1px solid var(--c-bg-4);
  }

  &.no-header .items-container {
    border-top: 1px solid var(--c-bg-4);
  }

  .extra-items-container {
    border-inline-start: 1px solid var(--c-bg-4);
    border-inline-end: 1px solid var(--c-bg-4);
    border-top: 1px solid var(--c-bg-4);
  }

  .cart-action-bar {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border: none !important;
  }

  .cart-item-unit-controls {
    max-width: 150px;
  }

  .cart-item {
    border-bottom: 1px solid var(--c-gray-2);

    &:last-child {
      border-bottom: none;
    }

    &:nth-child(2n) {
      background-color: var(--c-gray-1);
    }
  }

  .cart-item__image {
    object-fit: contain;
    max-width: 15vw;
    max-height: 150px;
  }

  .cart-item__desc-row {
    //align-items: center;
  }

  &.break-xs {
    .cart-item__image {
      border: 1px solid var(--c-bg-4);
      max-width: 14vw;
      max-height: 80px;
    }
  }

  .additional-item {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      border: 1px dashed var(--c-inverse);
      width: calc(100% - 20px);
      z-index:1;
      bottom: 20%;
    }
    .label {
      position: relative;
      z-index: 2;
      background: var(--c-gray-1);
      padding-inline-end: var(--margin-s);
    }
    .price {
      position: relative;
      z-index: 2;
      padding-inline-start: var(--margin-s);
      background: var(--c-gray-1);
      text-align: start;

      [dir=rtl] & {
        padding-inline-start: 0;
        padding-inline-end: var(--margin-s);
        text-align: right;
      }
    }
  }

  .pseudo-action-bar {
    border-top: 1px solid var(--c-bg-4);
  }
}
</style>
<style lang="scss"></style>
